import type { SocialMediaV2 } from "@/domains/company/company/api/types";
import { sanitizeSocialMedia } from "@/domains/company/company/utils/social-media";
import { isRenderableUrl } from "./url";

/**
 * The icon per social, and — being an array — the order they appear in.
 *
 * A map keyed by the network would render them in insertion order, which is the
 * order the user happened to fill the settings form in: the same company would
 * show a different row after every edit.
 */
const SOCIAL_ICONS = [
  ["twitter", "ph-x-logo"],
  ["github", "ph-github-logo"],
  ["instagram", "ph-instagram-logo"],
  ["facebook", "ph-facebook-logo"],
] as const satisfies readonly (readonly [string, string])[];

export type SocialLink = {
  key: string;
  url: string;
  icon: string;
};

/**
 * The social icon links of the identity card (AC 25).
 *
 * Built on `sanitizeSocialMedia`, which already drops the migrated `linkedin`
 * key (it has its own CTA from the `linkedin_url` column) and the dead `null`
 * keys the previous settings page left behind. What it does **not** do is
 * validate: it guarantees a live string, not a URL — so a company that typed
 * "@novantalabs" in the Instagram field would otherwise get an anchor pointing
 * at a relative path on our own site (EP6/EC21).
 *
 * Unknown keys are kept with a generic icon rather than hidden: the settings
 * page offers four networks today and may offer five tomorrow, and a link that
 * silently disappears is worse than one with a plain glyph.
 */
export const socialLinks = (
  social: SocialMediaV2 | null | undefined,
): SocialLink[] => {
  const clean = sanitizeSocialMedia(social);
  if (!clean) return [];

  const known = SOCIAL_ICONS.flatMap(([key, icon]) => {
    const url = clean[key];
    return isRenderableUrl(url) ? [{ key, url, icon }] : [];
  });

  const knownKeys = new Set<string>(SOCIAL_ICONS.map(([key]) => key));
  const extra = Object.entries(clean)
    .filter(([key]) => !knownKeys.has(key))
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([key, url]) =>
      isRenderableUrl(url) ? [{ key, url, icon: "ph-link-simple" }] : [],
    );

  return [...known, ...extra];
};
